import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import type { JSX } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import type { PersonnelDetailsDto } from '@ctms/contracts';

import { trialsApi } from '../trials.api.js';
import { PERSONNEL_CARD_LIST_QUERY, trialQueryKeys } from '../trials.query-keys.js';

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';
const labelClass = 'text-xs text-slate-400';

/**
 * The site personnel card as a single line for the `Osoba` list of choice. The
 * picker reads last name first ("NAZWISKO IMIĘ") so a list already sorted by last
 * name — see `comparePersonnelByName` — reads in order; every other place keeps
 * the first-name-first order. Both names of a card are nullable (legacy
 * `Personel_details`), so a nameless card falls back to its id instead of
 * rendering a blank `<option>`.
 */
export function personnelLabel(card: PersonnelDetailsDto): string {
  const parts = [card.lastName, card.firstName].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : `Osoba #${card.id}`;
}

export interface PersonnelSelectProps {
  /** Id tying the label to the control. */
  id: string;
  /**
   * Spread of `register('…')` — the owning dialog keeps the form field; the
   * select only fills it with the site's personnel cards. A string value (not
   * a coerced number) keeps the empty placeholder valid until a person is
   * picked, so the dialog converts to a number on submit.
   */
  registration: UseFormRegisterReturn;
  disabled?: boolean;
}

/**
 * "Osoba" — the personnel picker of the Domain 4 task/contact dialogs
 * (preflight OPCJA 1). The session identity carries no `personnel_details`
 * link (ADR-006), so every such dialog asks for the person explicitly from the
 * site's contact catalogue (`personnel:read`).
 */
export function PersonnelSelect({ id, registration, disabled = false }: PersonnelSelectProps): JSX.Element {
  const cardsQuery = useQuery({
    queryKey: trialQueryKeys.personnelCards(),
    queryFn: () => trialsApi.listPersonnel(PERSONNEL_CARD_LIST_QUERY),
    staleTime: 5 * 60 * 1000,
    retry: (count: number, err: unknown) => (isHTTPError(err) ? false : count < 3),
  });
  const cards = cardsQuery.data?.items ?? [];

  return (
    <>
      <label htmlFor={id} className={labelClass}>
        Osoba
      </label>
      <select id={id} className={fieldClass} disabled={disabled || cardsQuery.isLoading} {...registration}>
        <option value="">Wybierz osobę</option>
        {cards.map((card) => (
          <option key={card.id} value={String(card.id)}>
            {personnelLabel(card)}
          </option>
        ))}
      </select>
      {cardsQuery.isError ? (
        <p className="mt-1 text-xs text-amber-300">Nie udało się wczytać listy personelu.</p>
      ) : null}
    </>
  );
}